import { useMemo } from 'react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ServiceProvidersQueries } from '@frontend/api-service-providers';
import { DashboardWidget, DashboardWidgetFC } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SkeletonLoader, Text } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../hooks/use-scheduling-location-info';
import { getDateString } from '../utils/date-helpers';

const MAX_APPOINTMENTS = 999;

/**
 * @dashboard-widget
 *
 * id: appointment-count
 * title: Appointments
 * description: Show number of appointments scheduled for the day.
 * icon: calendar-small
 */
export const AppointmentCountWidget: DashboardWidgetFC = () => {
  const { selectedLocationIds } = useSchedulingLocationInfo();
  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { t } = useTranslation('dashboard');
  const { startDate, endDate } = useMemo(() => {
    const startDate = dayjs().startOf('day').toISOString();
    const endDate = dayjs().endOf('day').toISOString();
    return { startDate, endDate };
  }, []);

  const { data: appointmentsV2, isFetching: isLoadingV2Data } = ServiceProvidersQueries.useListAppointments({
    startDate,
    endDate,
    locationIds: selectedLocationIds,
    enabled: !isScheduleV3FlagEnabled && !isLoadingFF,
  });

  const { data: calendarEventsV3, isFetching: isLoadingV3Data } = SchedulerV3.Helpers.useGetCalendarEventsV3Data({
    selectedDate: getDateString(dayjs()),
    selectedLocationIds: selectedLocationIds,
    page: 1,
    limit: 1,
  });

  const appointmentCount =
    (isScheduleV3FlagEnabled ? calendarEventsV3?.totalCount : appointmentsV2?.appointments?.length) ?? 0;
  const isLoading = isLoadingV2Data || isLoadingV3Data;

  return (
    <DashboardWidget>
      <DashboardWidget.Header />
      <DashboardWidget.Content>
        {isLoading ? (
          <SkeletonLoader height={48} width='100%' />
        ) : (
          <Text weight='bold' css={{ fontSize: theme.fontSize(48), lineHeight: 1 }}>
            {appointmentCount > MAX_APPOINTMENTS ? `${MAX_APPOINTMENTS}+` : appointmentCount}
          </Text>
        )}
        <Text size='medium' color='subdued'>
          {t('Today')}
        </Text>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

AppointmentCountWidget.config = {
  size: 'tiny-slim',
  feature: 'schedule',
};
